import {globals, globalUser} from "./globals.js";
import {Games} from "../models/models.js";
import {monthNames} from "../ui/ui-helpers.js";
import {
    getSuperstarRoster,
    getSuperstarStreaks,
    getSuperstarWonTournaments
} from "../models/superstar.js";
import {getTagTeamRoster, getTagTeamStreaks, getTagTeamWonTournaments} from "../models/tagTeam.js";
import {getRosterCompany} from "../models/roster.js";
import {getSegmentName} from "./segments.js";
import {getMatchName} from "./matches.js";
import {getRankFromPopularity, getRosterMemberSuperstars} from "./superstars.js";
import {getTagTeamBrandName, getTagTeamPopularity} from "./tagTeams.js";

function myround(rating, base=0.25) {
    // For rounding to below 0.25, use decimalRound
    return base * Math.round(rating / base);
}

function decimalRound(num) {
    // Rounds to 2 decimal places
    return Math.round((num + Number.EPSILON) * 100) / 100;
}

function getRandomInt(min, max, random) {
    const randomToUse = random ? random : Math.random();
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(randomToUse * (max - min + 1) + min);
}

function getRandomUniform(min, max, random) {
    const randomToUse = random ? random : Math.random();
    return randomToUse * (max - min) + min;
}

function getRandomChoice(arr, random) {
    const randomToUse = random ? random : Math.random();
    return arr[Math.floor(randomToUse*arr.length)];
}

function getRandomSample(arr, size, randomFunction) {
    let shuffled = shuffle([...arr], randomFunction);
    return shuffled.slice(0, size);
}

function shuffle(array, randomFunction) {
    const randomToUse = randomFunction ? randomFunction : Math.random;
    let currentIndex = array.length, temporaryValue, randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {

        // Pick a remaining element...
        randomIndex = Math.floor(randomToUse() * currentIndex);
        currentIndex -= 1;

        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }

    return array;
}

function weightedChoice(options, randomInstance) {
    let i;
    let weights = [];
    const randomToUse = randomInstance ? randomInstance : Math.random();

    if (options.every(x => x[1] === 0)) {  // Add equal chance if all weights are 0
        for (let o of options) {
            o[1] = 1
        }
    }

    for (i = 0; i < options.length; i++)
        weights[i] = options[i][1] + (weights[i - 1] || 0);

    let random = randomToUse * weights[weights.length - 1];

    for (i = 0; i < weights.length; i++)
        if (weights[i] > random)
            break;

    return options[i][0];
}

function getTimeDiffHuman(startDate, dateNow) {
    let diffInMilliSeconds = Math.abs(startDate - dateNow) / 1000;

    const days = Math.floor(diffInMilliSeconds / 86400);
    diffInMilliSeconds -= days * 86400;
    const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
    diffInMilliSeconds -= hours * 3600;
    const minutes = Math.floor(diffInMilliSeconds / 60) % 60;
    diffInMilliSeconds -= minutes * 60;
    const seconds = Math.floor(diffInMilliSeconds) % 60;

    let difference = '';
    if (days > 0) {
        difference += (days === 1) ? `${days} day` : `${days} days`;
    }
    else if (hours > 0) {
        difference += (hours === 1) ? `${hours} hour` : `${hours} hours`;
    }
    else if (minutes > 0) {
        difference += (minutes === 1) ? `${minutes} minute` : `${minutes} minutes`;
    }
    else {
        difference += (seconds === 1) ? `${seconds} second` : `${seconds} seconds`;
    }

    return difference;
}

function getMonthsIndexDifference(endMonth, startMonth) {
    // Get difference between month indexes
    let monthsDifference;
    if (endMonth < startMonth) {
        monthsDifference = endMonth + 12 - startMonth;
    }
    else {
        monthsDifference = endMonth - startMonth;
    }
    return monthsDifference;
}

function toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

// String format function
if (!String.prototype.format) {
    String.prototype.format = function () {
        let args = arguments;
        return this.replace(/{(\d+)}/g, function (match, number) {
            return typeof args[number] != 'undefined'
                ? args[number]
                : match;
        });
    };
}

String.prototype.formatUnicorn = String.prototype.formatUnicorn ||
    function () {
        "use strict";
        var str = this.toString();
        if (arguments.length) {
            var t = typeof arguments[0];
            var key;
            var args = ("string" === t || "number" === t) ?
                Array.prototype.slice.call(arguments)
                : arguments[0];

            for (key in args) {
                str = str.replace(new RegExp("\\{" + key + "\\}", "gi"), args[key]);
            }
        }

        return str;
    };

function formatStringWithParams(string, params) {
  const names = Object.keys(params);
  const values = Object.values(params);
  return new Function(...names, `return \`${string}\`;`)(...values);
}

function commaSeparate(arr, separator) {
    if (typeof(separator) === 'undefined') separator = 'and';
    return arr.slice(0, -2).join(', ') +
        (arr.slice(0, -2).length ? ', ' : '') +
        arr.slice(-2).join(' ' + separator + ' ');
}

function areObjectArraysEmpty(obj) {
    for (const key of Object.keys(obj)) {
        if (obj[key].length > 0)
            return false;
    }
    return true;
}

function getMonthsWord(months) {
    if (months === 1) {
        return 'month'
    }
    return 'months'
}

function getYearsWord(years) {
    if (years === 1) {
        return 'year'
    }
    return 'years'
}

function getCurrentGame() {
    if (globals.game.id) {
        return Games.get(globals.game.id);
    }
    return null;
}

function objectInArray(obj, array) {
    // Check if id of object present in array of object ids
    if (obj.objectName) {
        array = array.filter(x => x.objectName === obj.objectName)  // only check in same collection
    }
    const ids = array.map(x => x.$loki);
    return ids.indexOf(obj.$loki) >= 0;
}

function getUniqueObjects(objects) {
    return objects.filter((value, index, self) =>
        index === self.findIndex((t) => (
            t.$loki === value.$loki && t.objectName === value.objectName
        ))
    );
}

function getUniqueObjectsByName(objects) {
    return objects.filter((value, index, self) =>
        index === self.findIndex((t) => (
            t.name === value.name
        ))
    );
}

function getRosterMemberPronoun(rosterMember, pronounType = null) {
    let pronouns = {
        'male': 'he',
        'female': 'she',
        'non-binary': 'they',
        'default': 'he'
    };
    if (pronounType === 'possessive') {
        pronouns = {
            'male': 'his',
            'female': 'her',
            'non-binary': 'their',
            'default': 'his'
        }
    }
    else if (pronounType === 'objective') {
        pronouns = {
            'male': 'him',
            'female': 'her',
            'non-binary': 'them',
            'default': 'him'
        }
    }
    // Get team's pronoun
    if (rosterMember.objectName === 'tagTeam') {
        return (pronouns['non-binary']);
    }
    // Get superstar's pronoun from gender
    else {
        return (pronouns[rosterMember.gender] || pronouns['default']);
    }
}

async function getTalentCompany(talent) {  // get_talent_company
    let talentCompany = null;

    let talentRoster;
    if (talent.objectName === 'superstar') {
        talentRoster = await getSuperstarRoster(talent)
    }
    else {
        talentRoster = await getTagTeamRoster(talent)
    }
    if (!talentRoster || ['indie', 'indieAvailable'].indexOf(talentRoster.type) >= 0) {
        return null
    }

    let talentRosterCompany = await getRosterCompany(talentRoster);
    if (talentRosterCompany) {
        talentCompany = talentRosterCompany
    }

    return talentCompany;
}

function getArrayMaxByProperty(array, property) {
    let res = Math.max.apply(Math, array.map(function (o) {
        return o[property];
    }));
    return array.find(function (o) {
        return o[property] === res;
    })
}

function getArrayMinByProperty(array, property) {
    let res = Math.min.apply(Math, array.map(function (o) {
        return o[property];
    }));
    return array.find(function (o) {
        return o[property] === res;
    })
}

function getRealMonth(month, startingMonth) {  // Previously get_real_month
    // Find current month by adding game's monthCount to startingMonth, return month name
    const startDate = new Date(1, startingMonth, 1);
    const endDate = new Date(startDate.setMonth(startDate.getMonth() + month));
    return monthNames[endDate.getMonth()].slice(0, 3);
}

function getRealDate(month, year, startingMonth, startingYear) {  // Previously get_real_date
    // Convert starting month and year to datetime, add months and years passed
    const startDate = new Date(startingYear, startingMonth, 1);
    let endDate = new Date(startDate.setFullYear(startDate.getFullYear() + year));
    endDate = new Date(endDate.setMonth(startDate.getMonth() + month));
    return `${monthNames[endDate.getMonth()].slice(0, 3)} ${endDate.getFullYear()}`;
}

function getFirstSunday(year, month) {
    let date = new Date(year, month, 1, 12);
    let weekday = date.getDay();
    let dayDiff = weekday === 0 ? 0 : 7 - weekday;
    return new Date(date.setDate(date.getDate() + dayDiff));
}

function generateName(gender = 'male', randomInstance = null, martian = false, usePatreonSuperstar = false) {
    const randomToUse = randomInstance ? randomInstance : Math.random();
    let name;

    // Add patreon superstar
    if (usePatreonSuperstar) {
        let patreonSuperstars = JSON.parse(localStorage.getItem('patreonSuperstarsData')) || [];
        if (patreonSuperstars.length) {
            let patreonSuperstar = getRandomChoice(patreonSuperstars);
            return patreonSuperstar.name;
        }
    }

    if (martian) {
        name = `${getRandomChoice(martianFirstNames, randomToUse)} ${getRandomChoice(martianLastNames, randomToUse)}`
    }
    else {
        let lastName = getRandomChoice(lastNames, randomToUse);
        if (gender === 'non-binary') {
            name = `${getRandomChoice(firstNames.male.concat(firstNames.female), randomToUse)} ${lastName}`
        }
        else {
            name = `${getRandomChoice(firstNames[gender], randomToUse)} ${lastName}`
        }
    }
    return name;
}

function romanizeNumber(num) {
    if (isNaN(num))
        return NaN;
    let digits = String(+num).split(""),
        key = ["","C","CC","CCC","CD","D","DC","DCC","DCCC","CM",
               "","X","XX","XXX","XL","L","LX","LXX","LXXX","XC",
               "","I","II","III","IV","V","VI","VII","VIII","IX"],
        roman = "",
        i = 3;
    while (i--)
        roman = (key[+digits.pop() + (i * 10)] || "") + roman;
    return Array(+digits.join("") + 1).join("M") + roman;
}

function getMonthsDifference(endMonth, endYear, startMonth, startYear) {
    return (endMonth + (endYear * 12)) - (startMonth + (startYear * 12))
}

function limitValueBetweenRange(value, min, max) {  // Previously limit_value_between_range
    if (value > max) {
        return max;
    }
    else if (value < min) {
        return min;
    }
    return value;
}

function findNameInString(name, string) {  // Previously find_name_in_string
    const re = new RegExp('\\b(' + name + ')\\b', 'i');
    return re.exec(string);
}

async function getRosterMemberStreaks(rosterMember) {
    if (rosterMember.objectName === 'superstar') {
        return getSuperstarStreaks(rosterMember)
    }
    else if (rosterMember.objectName === 'tagTeam') {
        return getTagTeamStreaks(rosterMember)
    }
}

async function getRosterMemberWonTournaments(rosterMember) {
    if (rosterMember.objectName === 'superstar') {
        return getSuperstarWonTournaments(rosterMember)
    }
    else if (rosterMember.objectName === 'tagTeam') {
        return getTagTeamWonTournaments(rosterMember)
    }
}

async function getRosterMemberRank(rosterMember, superstars) {  // get_rank
    let popularity = rosterMember.objectName === 'superstar' ? rosterMember.popularity :
        await getTagTeamPopularity(rosterMember, superstars);
    return getRankFromPopularity(popularity)
}

async function getRosterMemberPopularity(rosterMember, superstars) {  // get_roster_member_popularity
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.popularity;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        return getTagTeamPopularity(rosterMember, superstars)
    }
}

async function getRosterMemberAlignment(rosterMember) {
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.alignment;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return (s.every(x => x.alignment === s[0].alignment)) ? s[0].alignment : null;
    }
}

async function getRosterMemberBrandName(rosterMember) {
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.brandName;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        return getTagTeamBrandName(rosterMember);
    }
}

async function getRosterMemberOverness(rosterMember) {  // get_participant_overness
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.overness;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return Math.round(s.reduce((n, {overness}) => n + overness, 0) / s.length)
    }
}

async function getRosterMemberAge(rosterMember) {  // get_participant_age
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.age;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return Math.round(s.reduce((n, {age}) => n + age, 0) / s.length)
    }
}

async function getRosterMemberFaceOfTheCompany(rosterMember) {  // get_roster_member_fotc
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.faceOfTheCompany;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return s.every(x => x.faceOfTheCompany);
    }
}

async function getRosterMemberHot(rosterMember) {  // get_roster_member_hot
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.hot;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return s.every(x => x.hot);
    }
}

async function getRosterMemberStale(rosterMember) {  // get_roster_member_stale
    if (rosterMember.objectName === 'superstar') {
        return rosterMember.stale;
    }
    else if (rosterMember.objectName === 'tagTeam' || rosterMember.objectName === 'faction') {
        const s = await getRosterMemberSuperstars(rosterMember);
        return s.every(x => x.stale);
    }
}

function checkAreArraysEqual(array1, array2) {
    return array1.length === array2.length && array1.every((y) => array2.includes(y));
}

function checkAreObjArraysEqual(array1, array2) {
    return array1.length === array2.length && array1.every((y) => objectInArray(y, array2));
}

function getMatchOrSegmentName(item) {  // get_match_or_segment_name
    if (item.objectName === 'segment') {
        return getSegmentName(item)
    }
    else {
        return getMatchName(item)
    }
}

function getChunks(arr, len) {
    let chunks = [],
        i = 0,
        n = arr.length;
    while (i <= n - len) {
        chunks.push(arr.slice(i, i += len));
    }
    return chunks;
}

function isPowerOfTwo(x) {
    return (Math.log(x) / Math.log(2)) % 1 === 0;
}

function range(size, startAt = 0) {
    return [...Array(size).keys()].map(i => i + startAt);
}

function getHairColor() {  // get_hair_color
    const colorsList = [
        "0x502F0C", "0x733A00", "0x844B11", "0xA3672B", "0xC48647", "0xB04F1B",
        "0xFFFA84", "0x471E08", "0x999999", "0xEB8C4E", "0x090909", "0x3E3120",
        "0xDEBE99", "0x4C4C4C", "0xAA8866", "0x4F1A00", "0x9A3300", "0xE5E5E5",
        "0xC05D1C", "0xD5C427", "0xEBB25F"
    ];
    if (getRandomInt(0, 100) <= 90) {
        return getRandomChoice(colorsList);
    } else {
        return getRandomColor();
    }
}

function getRandomColor() {  // get_random_color
    const letters = '0123456789ABCDEF';
    let color = '0x';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

function getTalentAgeMultiplier(talent) {  // get_talent_age_multiplier
    if (talent.age <= 25) {
        return 1.5
    }
    else if (talent.age <= 32) {
        return 1.25
    }
    else if (talent.age <= 40) {
        return 1
    }
    else {
        return 0.5
    }
}

function getMoraleText(morale) {  // get_morale_text
    if (morale >= 60) {
        return 'Happy';
    } else if (morale >= 40) {
        return 'Content';
    } else if (morale >= 20) {
        return 'Dissatisfied';
    } else {
        return 'Unhappy';
    }
}

function getOvernessText(overness, isSuperstar = true) {
    if (overness >= 80) {
        if (isSuperstar) {
            return 'Over';
        } else {
            return 'Hot';
        }
    } else if (overness >= 60) {
        return 'Popular';
    } else if (overness >= 40) {
        return 'Average';
    } else if (overness >= 20) {
        return 'Not Popular';
    } else {
        return 'Stale';
    }
}

async function asyncFor(iters, callback) {
    for (let i of iters) {
        const promise = new Promise((resolve, _reject) => {
            setTimeout(async () => {
                await callback(i);
                resolve();
            }, 0);
        });
        await promise;
    }
}

async function fetchAsync(url) {
    let response = await fetch(url);
    return response.json();
}

function captureError(error) {
    console.log(error)
    if (typeof window.Sentry !== 'undefined') {
        Sentry.captureException(error);
    }
}

function generateUUID() {
    let
        d = new Date().getTime(),
        d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        let r = Math.random() * 16;
        if (d > 0) {
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        }
        else {
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x7 | 0x8)).toString(16);
    });
}

const staffRoles = ['doctor', 'scout', 'producer', 'trainer', 'talent_manager'];

const roleToFacilityData = {
    'doctor': 'Medical', 'scout': 'Scouting', 'producer': 'Production', 'trainer': 'Training',
    'talent_manager': 'Talent Management'
};

const facilityLevelCosts = {2: 200, 3: 450};

const gameLimits = {
    'maxCompetitors': 5,
    'maxEditorCompetitors': 24,
    'maxTraits': 10,
    'maxFactionSuperstars': 25,
    'maxRosterSuperstars': 500
}

const reputationXpLevels = {1: 0, 2: 10, 3: 30, 4: 60, 5: 100, 6: 150, 7: 210, 8: 280, 9: 360, 10: 450, 11: 550};

const fanLevels = [200, 500, 1100, 2300, 5000, 10500, 22000, 47000, 99000, 212000, 451000];

const facilitiesData = {
    'Medical': {
        'actionsToNextLevel': {2: 5, 3: 12},
        'actionName': 'Injuries Recovered',
        'nextLevelInfo': {
            1: 'The Developed (Level 2) Medical Facility unlocks the Rehab Facility for faster injury recovery ' +
               'and allows two Doctors up to Level 2 to work for you.',
            2: 'The Advanced (Level 3) Medical Facility unlocks an improved Rehab Facility for faster injury recovery ' +
               'and allows three Doctors up to Level 3 to work for you.',
            3: 'Your Advanced Medical Facility includes excellent rehab facilities and allows three Doctors up to Level 3 to ' +
               'work for you.'
        }
    },
    'Production': {
        'actionsToNextLevel': {2: 25, 3: 60},
        'actionName': 'Production Packages Produced',
        'nextLevelInfo': {
            1: 'The Developed (Level 2) Production Facility unlocks the Championship Gimmicks Facility to add ' +
               'gimmicks to custom championships, allows you to have more (+2) Matches and Segments on your ' +
               'shows and allows two Producers up to Level 2 to work for you.',
            2: 'The Advanced (Level 3) Production Facility unlocks the Focused Packages Facility to create custom ' +
               'production packages, allows you to have more (+4) Matches and Segments on your shows and allows ' +
               'three Producers up to Level 3 to work for you.',
            3: 'Your Advanced Production Facility includes more (+4) Matches and Segments on shows, the Championship ' +
               'Gimmicks Facility, the Focused Packages Facility and allows three Producers up to Level 3 to work for you.'
        }
    },
    'Scouting': {
        'actionsToNextLevel': {2: 8, 3: 20},
        'actionName': 'Superstars Scouted/Recruited',
        'nextLevelInfo': {
            1: 'The Developed (Level 2) Scouting Facility unlocks the Interest-building Facility for reducing indie ' +
               'signing costs and allows two Scouts up to Level 2 to work for you.',
            2: 'The Advanced (Level 3) Scouting Facility unlocks the Indie Tracking Facility to recruit new indie ' +
               'talent and allows three Scouts up to Level 3 to work for you.',
            3: 'Your Advanced Scouting Facility includes the Interest-building Facility, the Indie Tracking Facility ' +
               'and allows three Scouts up to Level 3 to work for you.'
        }
    },
    'Training': {
        'actionsToNextLevel': {2: 4, 3: 10},
        'actionName': 'Superstars Trained',
        'nextLevelInfo': {
            1: 'The Developed (Level 2) Training Facility increases Training Roster changes (Popularity, XP and ' +
               'Overness) and allows two Trainers up to Level 2 to work for you.',
            2: 'The Advanced (Level 3) Training Facility unlocks the Accelerated Training Facility to easily ' +
               'bring newly signed talent straight to the Main Roster, further increases Training Roster changes ' +
               '(Popularity, XP and Overness) and allows three Trainers up to Level 3 to work for you.',
            3: 'Your Advanced Training Facility includes the Accelerated Training Facility, increases Training Roster ' +
               'changes (Popularity, XP and Overness) and allows three Trainers up to Level 3 to work for you.'
        }
    },
    'Talent Management': {
        'actionsToNextLevel': {2: 15, 3: 35},
        'actionName': 'Superstars Reassured / Talent Requests Completed',
        'nextLevelInfo': {
            1: 'The Developed (Level 2) Talent Management Facility unlocks the Guaranteed Appearances Facility, ' +
               'allowing you to negotiate a minimum number of appearances with part-timers at the end of the year ' +
               'and allows two Talent Managers up to Level 2 to work for you.',
            2: 'The Advanced (Level 3) Talent Management Facility unlocks the Company Acquisition Facility, allowing ' +
               'you to buyout Competitors with certain conditions and allows three Talent Managers up to Level 3 to work for you.',
            3: 'Your Advanced Talent Management Facility includes the Guaranteed Appearances Facility, ' +
               'the Company Acquisition Facility and allows three Talent Managers up to Level 3 to work for you.'
        }
    }
};

const staffFirstNames = {
    'male': [
        "Aaron", "Adam", "Adil", "Alan", "Albert", "Alberto", "Alex", "Alexander", "Alfie", "Alfonso", "Amir",
        "Angel", "Anthony", "Austin", "Axel", "Bartolomeu", "Benjamin", "Billy", "Bobby", "Brian", "Bruce", "Bruno",
        "Bryan", "Carl", "Cesar", "Charles", "Christian", "Christopher", "Craig", "Damien", "Daniel", "Dario",
        "David", "Dominic", "Donald", "Doug", "Drake", "Dylan", "Ed", "Eddie", "Edgar", "Eduardo", "Eli", "Emanuel",
        "Emin", "Eric", "Eugene", "Evil", "Fabio", "Fernando", "Florian", "Frank", "Gaspard", "George", "Gerald",
        "Gregory", "Gus", "Gustavo", "Harry", "Hayden", "Henry", "Howard", "Hugo", "Igor", "Ivan", "Jack", "Jaime",
        "James", "Jase", "Jason", "Jay", "Jerry", "Jesse", "Jimmy", "Joe", "John", "Johnathan", "Johnny", "Jordan",
        "Jose", "Juan", "Julian", "Justin", "Karl", "Keith", "Kenneth", "Kevin", "Kyle", "Larry", "Leonard",
        "Louis", "Lucas", "Marco", "Marin", "Marlon", "Matt", "Max", "Michael", "Mohit", "Muhammad", "Nate",
        "Nathan", "Nicolas", "Nolan", "Octavian", "Omer", "Oscar", "Pat", "Paul", "Pedro", "Philip", "Phoenix",
        "Rafael", "Rahul", "Randy", "Raymond", "Rhys", "Richard", "Riley", "Rob", "Robin", "Rocky", "Roger",
        "Russell", "Ryan", "Sabir", "Sam", "Sami", "Samuel", "Scott", "Sean", "Sebastian", "Silvester", "Simon",
        "Stan", "Steven", "Terry", "Theo", "Tim", "Tom", "Trent", "Tyler", "Victor", "Vince", "Vladimir",
        "Vladislav", "Wayne", "William", "Willie", "Zachary",
        "Fab", "Mad", "Mr. "
    ],
    'female': [
        "Alice", "Amanda", "Amber", "Amelie", "Amy", "Anabela", "Andrea", "Angela", "Ann", "Anna", "Ashley",
        "Aubree", "Barbara", "Betty", "Beverly", "Brenda", "Brittany", "Carmen", "Carol", "Carolyn", "Catherine",
        "Cheryl", "Christina", "Christine", "Claudia", "Crystal", "Cynthia", "Danielle", "Deborah", "Debra",
        "Deepa", "Denise", "Diana", "Diane", "Donna", "Doris", "Dorothy", "Elizabeth", "Emily", "Emma", "Eva",
        "Evelyn", "Frances", "Gloria", "Grace", "Hannah", "Heather", "Helen", "Helena", "Imogen", "Jacqueline",
        "Jane", "Janet", "Janice", "Jasmine", "Jean", "Jennifer", "Jessica", "Joan", "Joyce", "Judith", "Judy",
        "Julia", "Julie", "Karen", "Katherine", "Kathleen", "Kathryn", "Kathy", "Kelly", "Kimberly", "Laura",
        "Lauren", "Linda", "Lisa", "Lola", "Lori", "Luna", "Madison", "Margaret", "Maria", "Marie", "Marilyn",
        "Marketa", "Martha", "Mary", "Megan", "Melania", "Melissa", "Mia", "Michelle", "Mildred", "Mina", "Monica",
        "Nancy", "Natalia", "Nicole", "Olivia", "Pamela", "Patricia", "Peyton", "Pooja", "Priya", "Rachel",
        "Rebecca", "Rose", "Ruth", "Samantha", "Sandra", "Sara", "Sarah", "Sharon", "Shirley", "Sofia", "Sophia",
        "Stephanie", "Summer", "Susan", "Tammy", "Tayla", "Teresa", "Tessa", "Theresa", "Theresa", "Tiffany",
        "Valerie", "Victoria", "Virginia",
        "Mrs. ", "Candy", "Indigo", "Dandelion"
    ]
};

const staffLastNames = [
    "Adams", "Alexander", "Allen", "Alvarez", "Anderson", "Andrews",
    "Armstrong", "Arnold", "Austin", "Bailey", "Baker", "Banks", "Barnes", "Barnett", "Barrett",
    "Bates", "Beck", "Bell", "Bennett", "Black", "Bowman", "Bradley",
    "Brewer", "Brooks", "Brown", "Bryant", "Burke", "Burns", "Burton", "Campbell",
    "Carlson", "Carpenter", "Carr", "Carroll", "Carter", "Castillo", "Castro", "Chapman", "Chavez",
    "Chen", "Clark", "Cole", "Coleman", "Collins", "Contreras", "Cook", "Cooper", "Cox",
    "Crawford", "Cruz", "Cunningham", "Curtis", "Daniels", "Davis", "Day", "Dean",
    "Delgado", "Diaz", "Dixon", "Douglas", "Duncan", "Dunn", "Edwards", "Elliott", "Ellis",
    "Estrada", "Evans", "Fernandez", "Fields", "Fisher", "Flores", "Ford", "Foster",
    "Fox", "Fuller", "Garcia", "Gardner", "Garrett", "Garza",
    "George", "Gomez", "Gonzales", "Gonzalez", "Gordon", "Graham", "Grant",
    "Gray", "Green", "Greene", "Guerrero", "Gutierrez", "Hall", "Hamilton",
    "Hansen", "Hanson", "Harper", "Harris", "Harrison", "Hart", "Harvey", "Hawkins", "Hayes",
    "Henderson", "Henry", "Hernandez", "Herrera", "Hicks", "Hill", "Holland", "Holmes",
    "Hopkins", "Howard", "Howell", "Hughes", "Hunt", "Hunter", "Jackson", "Jacobs",
    "James", "Jenkins", "Jensen", "Johnson", "Jones", "Jordan", "Keller",
    "Kennedy", "Kim", "King", "Knight", "Lane", "Larson",
    "Lee", "Lewis", "Little", "Long", "Lopez", "Lucas", "Lynch", "Marshall", "Martinez",
    "Mason", "Matthews", "May", "McDonald", "Medina", "Mendez", "Mendoza", "Meyer",
    "Miller", "Mills", "Mitchell", "Moore", "Morales", "Moreno", "Morgan", "Morris",
    "Morrison", "Munoz", "Murray", "Myers", "Nelson", "Newman", "Nguyen", "Nichols",
    "Oliver", "Olson", "Ortega", "Ortiz", "Owens", "Palmer", "Parker", "Patel",
    "Patterson", "Payne", "Pearson", "Perkins", "Perry", "Peters", "Peterson",
    "Phillips", "Pierce", "Porter", "Powell", "Price", "Ramirez", "Ramos", "Ray", "Reed", "Reid",
    "Reyes", "Reynolds", "Rice", "Richards", "Richardson", "Riley", "Rios", "Roberts",
    "Robertson", "Robinson", "Rodriguez", "Rogers", "Romero", "Rose", "Ross", "Ruiz", "Russell",
    "Ryan", "Sanchez", "Sanders", "Sandoval", "Santos", "Schmidt", "Schneider",
    "Schultz", "Silva", "Simmons", "Simpson", "Sims", "Smith", "Snyder",
    "Spencer", "Stanley", "Stephens", "Stevens", "Stewart", "Stone", "Sullivan", "Taylor",
    "Thomas", "Thompson", "Torres", "Tran", "Tucker", "Turner", "Valdez", "Vargas", "Vasquez",
    "Wade", "Wagner", "Walker", "Wallace", "Walsh", "Walters", "Ward", "Washington",
    "Watkins", "Watson", "Weaver", "Webb", "Weber", "Wells", "West", "Wheeler", "White",
    "Williams", "Williamson", "Willis", "Wilson", "Wong", "Wood", "Woods", "Wright", "Young"
];

const staffMartianFirstNames = [
    "Able", "Araqr", "Atthel", "Avin", "Brickev", "Chol", "Chrone", "Clipatr", "Dennio",
    "Digital", "El", "Eldan", "Enjan", "Eoo", "Evin", "Feri", "Fire", "Fryan",
    "Garey", "Gory", "Harlo", "Hawne", "Hawnio", "Hebjam", "Hery", "Jery", "Johne", "Jonty",
    "Juany", "Kham", "Koni", "Lary", "Lovin", "Maester", "Maqzuz", "Mertutu", "Michy", "Mondy",
    "Muely", "Nichua", "Nitrogen", "Orange", "Oxygen", "Priest", "Pule", "Red", "Reverend", "Rog",
    "Roni", "Sharly", "Stimy", "Stine", "Stoddy", "Tauvav",
    "Thenry", "Theward", "Topu", "Turi", "Waltodd", "Waynio"
];

const staffMartianLastNames = [
    "Ales", "Anchez", "Ashins", "Aved", "Bande", "Binson", "Briffost", "Campa", "Carte",
    "Clema", "Coopat", "Coopow", "Derson", "Drusske", "Earthling", "Ener", "Finer",
    "Flare", "Grobak", "Guezal", "Henders", "Hingte", "Holo", "Homan",
    "Jamart", "Jamit", "Jimin", "Kingonz", "Lente", "Lexand", "Lezal", "Longonz", "Lopark",
    "Lory", "Maner", "Miray", "Pari", "Patte", "Per",
    "Powash", "Power", "Price", "Priver", "Ramorr", "Reeder", "Reson", "River", "Rodre",
    "Rogonz", "Rossanch", "Rowner", "Ryantez", "Scoley", "Smarte", "Smil", "Smoor",
    "Sones", "Thill", "Torra", "Wanch", "Wardams", "Wison", "Woodez", "Yournell", "Zene"
];

const staffHiredRamblings = [
    {
        'subject': 'Excited!',
        'content': 'I\'m excited to get to work! Thanks for hiring me.'
    },
    {
        'subject': 'Ready',
        'content': 'I\'m ready to get to work. Let me know what you need and I\'ll do my best.'
    },
    {
        'subject': 'Thanks',
        'content': 'Thank you for giving me a shot. I won\'t let you down.'
    },
    {
        'subject': 'New start',
        'content': 'I\'m so glad to have this new start. It was hard being an independent contractor.'
    },
    {
        'subject': 'Hey!',
        'content': 'I\'m so ready to start! Let\'s do this!'
    },
    {
        'subject': 'Thank you',
        'content': 'I wanted to formally thank you for hiring me. I will try my best to live up to your expectations.'
    },
    {
        'subject': 'Work',
        'content': 'I\'m ready for work. Let me know what my first job is.'
    },
    {
        'subject': 'Lovely place',
        'content': 'This is such a lovely place to work at! I\'m really happy to be here.'
    }
];

const staffFiredRamblings = [
    {
        'subject': 'What happened?',
        'content': 'I thought things were going really well? Really, I did. I don\'t know what happened ..'
    },
    {
        'subject': 'Let go',
        'content': 'I understand you had to let me go. I won\'t hold it against you. Good luck for the future.'
    },
    {
        'subject': 'Scared',
        'content': 'I\'m a little scared of going back to being an independent contractor. But I got by before, and hey, maybe something better will come along?'
    },
    {
        'subject': 'Fired?!',
        'content': 'I can\'t believe you let me go! Your company is going to break down without me! You made the biggest mistake of your career.'
    },
    {
        'subject': 'What?!',
        'content': 'You fired me?! You\'re crazy to do this! I\'m going to make you pay.'
    },
    {
        'subject': 'Disappointed',
        'content': 'I\'m really disappointed you let me go. I\'m going to go to a company that treats their staff better.'
    },
    {
        'subject': 'Stupid',
        'content': 'Letting me go was a stupid decision on your part. You\'re going to regret it one day.'
    },
    {
        'subject': 'Sorry',
        'content': 'I\'m sorry for letting you down. I guess I have to go now. I really thought I fit in here ..'
    }
];

const eventPostersData = [{'type': 1, 'numberOfSuperstars': 9},
    {'type': 2, 'numberOfSuperstars': 6},
    {'type': 3, 'numberOfSuperstars': 5},
    {'type': 4, 'numberOfSuperstars': 3}];

const annualTournamentPostersData = [{'type': 5, 'numberOfSuperstars': 16},
    {'type': 6, 'numberOfSuperstars': 16},
    {'type': 7, 'numberOfSuperstars': 16}];

const championshipPrestigeXps = {'S': 800, 'A': 300, 'B': 100, 'C': 0};
const eventPrestigeXps = {'A': 110, 'B': 40, 'C': 0};

const firstNames = {
    'male': ["Aaron", "Adam", "Alan", "Albert", "Alexander", "Alex", "Anthony", "Austin",
        "Benjamin", "Billy", "Bobby", "Brian", "Bruce", "Bryan", "Carl", "Charles", "Christian",
        "Christopher", "Craig", "Daniel", "David", "Donald", "Doug", "Dylan", "Ed", "Eddie",
        "Eric", "Eugene", "George", "Gerald", "Gregory", "Harry", "Felix", "Sabir", "Sami",
        "Henry", "Howard", "Jack", "James", "Jason", "Jerry", "Jesse", "Joe", "Lucas", "Adil", "Marin",
        "John", "Johnny", "Jordan", "Jose", "Juan", "Justin", "Keith", "Kevin", "Kyle", "Larry", "Matt",
        "Pat", "Paul", "Philip", "Randy", "Raymond", "Angel", "Victor", "Igor", "Gaspard", "Theo",
        "Richard", "Rob", "Roger", "Russell", "Ryan", "Sam", "Scott", "Sean", "Jay", "Evil",
        "Steven", "Terry", "Tom", "Tim", "Tyler", "Vince", "Wayne", "Karl", "Bruno", "Paul", "Muhammad",
        "William", "Willie", "Zachary", "Stan", "Drake", "Trent", "Velvet", "Omer", "Michael",
        "Nathan", "Selim", "Magnus", "Vladimir", "Gus", "Eli", "Jase", "Riley", "Nate", "Nolan", "Oscar",
        "Cesar", "Dario", "Rocky", "Max", "Axel", "Alberto", "Rhys", "Dominic", "Julian", "Phoenix"],
    'female': ["Alice", "Amanda", "Amber", "Amy", "Andrea", "Angela", "Ann", "Anna", "Ashley", "Barbara", "Betty",
        "Beverly", "Brenda", "Brittany", "Carol", "Carolyn", "Catherine", "Cheryl", "Christina", "Christine",
        "Crystal", "Cynthia", "Danielle", "Deborah", "Debra", "Denise", "Diana", "Diane", "Donna", "Doris",
        "Dorothy", "Elizabeth", "Emily", "Emma", "Evelyn", "Frances", "Gloria", "Grace", "Hannah", "Heather",
        "Helen", "Jacqueline", "Jane", "Janet", "Janice", "Jean", "Jennifer", "Jessica", "Joan", "Joyce",
        "Judith", "Judy", "Julia", "Julie", "Karen", "Katherine", "Kathleen", "Kathryn", "Kathy", "Kelly",
        "Kimberly", "Laura", "Lauren", "Linda", "Lisa", "Lori", "Madison", "Margaret", "Maria", "Marie",
        "Marilyn", "Martha", "Mary", "Megan", "Melissa", "Michelle", "Mildred", "Nancy", "Nicole", "Olivia",
        "Pamela", "Patricia", "Rachel", "Rebecca", "Rose", "Ruth", "Samantha", "Sandra", "Sara", "Sarah",
        "Sharon", "Shirley", "Stephanie", "Sophia", "Susan", "Tammy", "Teresa", "Theresa", "Tiffany",
        "Victoria", "Virginia", "Queen", "Miss", "Ela", "Io", "Juniper", "Taya", "Rose", "Tessa", "Tayla",
        "Trinity", "Violet", "Savannah", "Imogen", "Summer", "Leah", "Jordyn", "Lea", "Lylah", "Aleah",
        "Aubree", "Isla", "Paisley", "Peyton", "Dahlia", "Sombra", "Eden", "Kitsune", "Azalea"]
};

const lastNames = ["Adams", "Alexander", "Allen", "Alvarez", "Anderson", "Andrews",
    "Armstrong", "Arnold", "Austin", "Bailey", "Baker", "Banks", "Barnes", "Barnett", "Barrett",
    "Bates", "Beck", "Bell", "Bennett", "Black", "Bowman", "Bradley",
    "Brewer", "Brooks", "Brown", "Bryant", "Burke", "Burns", "Burton", "Campbell",
    "Carlson", "Carpenter", "Carr", "Carroll", "Carter", "Castillo", "Castro", "Chapman", "Chavez",
    "Chen", "Clark", "Cole", "Coleman", "Collins", "Contreras", "Cook", "Cooper", "Cox",
    "Crawford", "Cruz", "Cunningham", "Curtis", "Daniels", "Davis", "Day", "Dean",
    "Delgado", "Diaz", "Dixon", "Douglas", "Duncan", "Dunn", "Edwards", "Elliott", "Ellis",
    "Estrada", "Evans", "Fernandez", "Fields", "Fisher", "Flores", "Ford", "Foster",
    "Fox", "Fuller", "Garcia", "Gardner", "Garrett", "Garza",
    "George", "Gomez", "Gonzales", "Gonzalez", "Gordon", "Graham", "Grant",
    "Gray", "Green", "Greene", "Guerrero", "Gutierrez", "Hall", "Hamilton",
    "Hansen", "Hanson", "Harper", "Harris", "Harrison", "Hart", "Harvey", "Hawkins", "Hayes",
    "Henderson", "Henry", "Hernandez", "Herrera", "Hicks", "Hill", "Holland", "Holmes",
    "Hopkins", "Howard", "Howell", "Hughes", "Hunt", "Hunter", "Jackson", "Jacobs",
    "James", "Jenkins", "Jensen", "Johnson", "Jones", "Jordan", "Keller",
    "Kennedy", "Kim", "King", "Knight", "Lane", "Larson",
    "Lee", "Lewis", "Little", "Long", "Lopez", "Lucas", "Lynch", "Marshall", "Martinez",
    "Mason", "Matthews", "May", "McDonald", "Medina", "Mendez", "Mendoza", "Meyer",
    "Miller", "Mills", "Mitchell", "Moore", "Morales", "Moreno", "Morgan", "Morris",
    "Morrison", "Munoz", "Murray", "Myers", "Nelson", "Newman", "Nguyen", "Nichols",
    "Oliver", "Olson", "Ortega", "Ortiz", "Owens", "Palmer", "Parker", "Patel",
    "Patterson", "Payne", "Pearson", "Perkins", "Perry", "Peters", "Peterson",
    "Phillips", "Pierce", "Porter", "Powell", "Price", "Ramirez", "Ramos", "Ray", "Reed", "Reid",
    "Reyes", "Reynolds", "Rice", "Richards", "Richardson", "Riley", "Rios", "Roberts",
    "Robertson", "Robinson", "Rodriguez", "Rogers", "Romero", "Rose", "Ross", "Ruiz", "Russell",
    "Ryan", "Sanchez", "Sanders", "Sandoval", "Santos", "Schmidt", "Schneider",
    "Schultz", "Silva", "Simmons", "Simpson", "Smith", "Snyder",
    "Spencer", "Stanley", "Stephens", "Stevens", "Stewart", "Stone", "Sullivan", "Taylor",
    "Thomas", "Thompson", "Torres", "Tran", "Tucker", "Turner", "Valdez", "Vargas", "Vasquez",
    "Wade", "Wagner", "Walker", "Wallace", "Walsh", "Walters", "Ward", "Washington",
    "Watkins", "Watson", "Weaver", "Webb", "Weber", "Wells", "West", "Wheeler", "White",
    "Williams", "Williamson", "Willis", "Wilson", "Wong", "Wood", "Woods", "Wright", "Young",
    "Crude", "Watts", "Black", "Crusher", "Flye", "Dragon", "Carnage", "Storm", "Bane",
    "Bukhari", "Wyatt", "Bourne", "Cat", "Lethal", "Jr", "Sr", "Hero", "Strong",
    "Pain", "Kong", "Butcher", "Cabana", "Red", "Blue", "Tiger", "Gunn",
    "Slinger", "Spot", "Studd", "Angel", "Koloff", "Punisher", "Ice", "Storm", "Ray",
    "Kid", "Jack", "Frye", "Flame", "Rocker", "Lee", "Horowitz", "Rich", "Rose",
    "Regal", "Cannon", "Wolfman", "Dust", "Dream", "the Third", "Hawke", "Flowers",
    "Durante", "Gallows", "Graves", "Rain", "Beast"];

const martianFirstNames = ["Able", "Araqr", "Atthel", "Avin", "Brickev", "Chol", "Chrone", "Clipatr", "Dennio",
    "Digital", "El", "Eldan", "Enjan", "Eoo", "Evin", "Feri", "Fire", "Fryan",
    "Garey", "Gory", "Harlo", "Hawne", "Hawnio", "Hebjam", "Hery", "Jery", "Johne", "Jonty",
    "Juany", "Kham", "Koni", "Lary", "Lovin", "Maester", "Maqzuz", "Mertutu", "Michy", "Mondy",
    "Muely", "Nichua", "Nitrogen", "Orange", "Oxygen", "Priest", "Pule", "Red", "Reverend",
    "Rog", "Roni", "Sharly", "Sol", "Solar", "Space", "Stimy", "Stine", "Stoddy", "Tauvav",
    "The", "The", "The", "Thenry", "Theward", "Topu", "Turi", "Venutian", "Virtual", "Waltodd",
    "Waynio"];

const martianLastNames = ["Ales", "Anchez", "Ashins", "Aved", "Bande", "Binson", "Briffost", "Campa", "Carte",
    "Clema", "Coopat", "Coopow", "Derson", "Drusske", "Earthling", "Ener", "Energy", "Finer",
    "Flare", "Freeze", "God", "Grobak", "Guezal", "Henders", "Hingte", "Holo", "Homan",
    "Jamart", "Jamit", "Jimin", "Kingonz", "Lente", "Lexand", "Lezal", "Longonz", "Lopark",
    "Lory", "Man", "Maner", "Meteor", "Miray", "Mission", "Oxide", "Pari", "Patte", "Per",
    "Powash", "Power", "Price", "Priver", "Ramorr", "Reeder", "Reson", "River", "Rodre",
    "Rogonz", "Rossanch", "Rowner", "Ryantez", "Scoley", "Sensation", "Smarte", "Smil", "Smoor",
    "Sones", "Sones", "Supply", "Thill", "Torra", "Virus", "Voyager", "Wanch", "Wardams",
    "Wison", "Woodez", "Yournell", "Zene"];

const goalProperties = {
    'rating': ['events', 'matches', 'gimmick matches'],
    'company': ['holy shit moments', 'main eventers signed', 'new championship', 'win awards', 'win moty',
        'win eoty', 'win woty', 'win gmoty'],
    'superstar': ['win battle royal', 'win championship contract', 'face of the company', 'main eventer',
        'new tag team', 'get over', 'new trio']
};

export { myround, getRandomInt, getTimeDiffHuman, commaSeparate, getMonthsIndexDifference, toTitleCase,
    areObjectArraysEmpty, getMonthsWord, getRandomUniform, getRandomSample,
    getCurrentGame, getRandomChoice, shuffle, objectInArray, getUniqueObjects, getRosterMemberPronoun,
    getArrayMaxByProperty, formatStringWithParams, getRealMonth, getFirstSunday, staffRoles, getRealDate,
    roleToFacilityData, facilitiesData, facilityLevelCosts, staffFirstNames, staffLastNames, staffMartianFirstNames,
    staffMartianLastNames, staffHiredRamblings, staffFiredRamblings, generateName, romanizeNumber, eventPostersData,
    annualTournamentPostersData, weightedChoice, getMonthsDifference, limitValueBetweenRange, findNameInString,
    getRosterMemberStreaks, getUniqueObjectsByName, championshipPrestigeXps,
    getTalentCompany, getRosterMemberWonTournaments, checkAreArraysEqual, decimalRound,
    getMatchOrSegmentName, getRosterMemberRank, checkAreObjArraysEqual, getChunks, getRosterMemberPopularity,
    getRosterMemberAlignment, getRosterMemberBrandName, isPowerOfTwo, range, getArrayMinByProperty,
    getRosterMemberOverness, getHairColor, getMoraleText, getOvernessText, getTalentAgeMultiplier, getRosterMemberHot,
    getRosterMemberStale, getRosterMemberFaceOfTheCompany, getRosterMemberAge, getRandomColor,
    reputationXpLevels, fanLevels, goalProperties, asyncFor, gameLimits, fetchAsync, captureError, generateUUID,
    getYearsWord, eventPrestigeXps}